// «Show me where»: an anonymized screenshot of a third-party screen with accent boxes and numbered pins,
// and a numbered caption under it (the mockups' guide figures, ADR-0032). Positions are percentages of
// the image, copied from the mockup; the images and their redaction maps live in
// docs/design/2026-10-04-connections-ux/assets/guides/.
import type { ReactNode } from "react";
import { CaretDownIcon } from "./icons";

/** A highlighted area, in % of the image. */
export interface FigureBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** A numbered pin at a point of the image, in %; `shift` is the badge's own offset (default −55% −55%). */
export interface FigurePin {
  n: number;
  left: number;
  top: number;
  shift?: [number, number];
}

/** Intrinsic sizes of the screenshots in web/public/guides/: width and height on <img> reserve the space before
 * the image loads (no layout shift); CSS keeps width: 100%, height: auto. */
const GUIDE_IMAGE_SIZE: Record<string, [number, number]> = {
  "/guides/app-store-connect-team-keys.png": [1280, 556],
  "/guides/appsflyer-security-center.png": [2740, 1215],
  "/guides/chatgpt-plugin-connect.png": [482, 502],
  "/guides/chatgpt-plugin-install.png": [766, 370],
  "/guides/claude-plugin-connect.png": [1020, 315],
  "/guides/claude-plugins-add-marketplace.png": [900, 460],
  "/guides/claude-plugins-search.png": [900, 200],
};

/** The same numbered badge as on the image, for captions and field labels. */
export function PinBadge({ n, small = false }: { n: number; small?: boolean }) {
  return (
    <span className={small ? "pin-badge small" : "pin-badge"} aria-hidden="true">
      {n}
    </span>
  );
}

export function GuideFigure({
  summary,
  src,
  alt,
  boxes,
  pins,
  caption,
  open = true,
  wide = false,
  testId,
}: {
  summary: string;
  src: string;
  alt: string;
  boxes: FigureBox[];
  pins: FigurePin[];
  /** One line per pin, in pin order; a single line is shown without a list. */
  caption: ReactNode[];
  /** Guide figures on the main path start open (design changelog 2026-10-04). */
  open?: boolean;
  /** 720px instead of 560px (the Team Keys page is a wide screenshot). */
  wide?: boolean;
  testId?: string;
}) {
  return (
    <details className={wide ? "guide-figure wide" : "guide-figure"} open={open} data-testid={testId}>
      <summary>
        <CaretDownIcon size={12} className="guide-figure-caret" />
        {summary}
      </summary>
      <figure>
        <div className="guide-figure-shot">
          <img src={src} alt={alt} width={GUIDE_IMAGE_SIZE[src]?.[0]} height={GUIDE_IMAGE_SIZE[src]?.[1]} loading="lazy" />
          {boxes.map((b, i) => (
            <span
              key={`b${i}`}
              className="guide-box"
              style={{ left: `${b.left}%`, top: `${b.top}%`, width: `${b.width}%`, height: `${b.height}%` }}
            />
          ))}
          {pins.map((p) => (
            <span
              key={`p${p.n}`}
              className="guide-pin"
              aria-hidden="true"
              style={{
                left: `${p.left}%`,
                top: `${p.top}%`,
                transform: `translate(${p.shift?.[0] ?? -55}%, ${p.shift?.[1] ?? -55}%)`,
              }}
            >
              {p.n}
            </span>
          ))}
        </div>
        <figcaption>
          {caption.map((c, i) => (
            <span key={i} className="guide-caption-line">
              <PinBadge n={pins[i]?.n ?? i + 1} small />
              <span>{c}</span>
            </span>
          ))}
        </figcaption>
      </figure>
    </details>
  );
}
